import React, { useContext, useEffect, useState, useRef } from "react";
import ReactDOM from "react-dom";
import { Box, IconButton, useMediaQuery } from "@mui/material";
import { Images } from "./../../assets/common";
import Slider from "@mui/material/Slider";
import { useHistory } from "react-router-dom";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeDownRoundedIcon from "@mui/icons-material/VolumeDownRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import QueueMusicRoundedIcon from "@mui/icons-material/QueueMusicRounded";
import PlaylistAddRoundedIcon from "@mui/icons-material/PlaylistAddRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import RepeatOneRoundedIcon from "@mui/icons-material/RepeatOneRounded";
import PlayQueue from "./../../pages/PlayQueue";

import "./index.scss";
import {
  fullName,
  getErrorString,
  getUserId,
  isOwnerId,
  hasPremium,
  HMS,
} from "../../constants/function";
import Loader from "./../../components/Loader";
import notifier from "./../../components/Notifier";
import ApiService from "./../../services/api-service";
import { createPlayTracking } from "./playTracking";
import { rememberShownQueue, songToWaitOn } from "./playerRebuild";
import TipPopup from "../TipPopup";
import { getBalance } from "../../asyncAction/asyncBalance";
import { useDispatch, useSelector } from "react-redux";
import AddMusicToPlaylist from "../AddMusicToPlaylist";
import { addSongIndexAction } from "./../../action/songs";
import { updatecount } from "../../action/adv";
import { SocketContext } from "../../context/socket";
import { useTranslation } from "react-i18next";
import Waveform from "./waveform";
import WaveSurferPlayer from "./WaveSurferPlayer";
import MobileNowPlaying from "./MobileNowPlaying";
import usePlayableUrl from "../../hooks/usePlayableUrl";
import FavoriteButton from "../../common/FavoriteButton";
import AIBadge from "../AIBadge";
import { palette } from "../../theme/tokens";

// Below this width the bar keeps its original two-row mobile layout.
const MOBILE_QUERY = "(max-width: 767.98px)";
// Free-tier listeners get a direct-sold ad break after this many songs finish on their own.
const AD_EVERY_N_AUTO_ADVANCES = 3;
export default function Player({
  songs,
  songIndex,
  all,
  prevSongIndex,
  showAdv,
}) {
  const { t } = useTranslation("common");
  const [seek, setSeek] = React.useState({ started: false, value: null });
  const [showPlaylist, setShowPlaylist] = React.useState(false);
  const [selectedId, setSelectedAlbum] = React.useState();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  // The queue button the popup opened from; null while the queue is closed.
  const [queueAnchor, setQueueAnchor] = useState(null);
  const barRef = useRef(null);
  const mobileQueueButtonRef = useRef(null);
  const isMobile = useMediaQuery(MOBILE_QUERY, { noSsr: true });

  const [volume, setVolume] = useState(100);
  const lastVolumeRef = useRef(100);
  const [song, setSong] = useState([]);
  const [currentTime, setCurrentTime] = useState(0);
  const [index, setIndex] = useState(songIndex ? songIndex : 0);
  const history = useHistory();
  const [track, setTrack] = useState(null);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [loading, setLoading] = useState(false);
  const [repeat, setRepeat] = useState(() => {
    const savedRepeat = localStorage.getItem('playerRepeatMode');
    return savedRepeat ? parseInt(savedRepeat) : 0;
  });
  const { count } = useSelector((state) => state.adv);
  const isPremium = hasPremium();
  const socket = useContext(SocketContext).Socket;
  const [songPlaying, setSongPlaying] = useState(null);
  const [currentPlayTime, setCurrentPlayTime] = useState(0);
  const [newPlayTime, setNewPlayTime] = useState(0);
  const currentPlayTimeRef = useRef(0);
  const playTracking = useRef(createPlayTracking(ApiService)).current;
  const [userSaves, setUserSaves] = useState([]);
  // Signed audio links expire; a failed one is swapped for a fresh link and
  // the song picks up where it stopped.
  const { url: playableUrl, refresh: refreshPlayableUrl } = usePlayableUrl(song[index]);
  const [resumeAt, setResumeAt] = useState({ songId: null, time: 0 });
  const handleMediaError = ({ currentTime }) => {
    const songId = song[index]?._id;
    refreshPlayableUrl().then((fresh) => {
      if (fresh) setResumeAt({ songId, time: currentTime });
    });
  };
  // Songs that ended on their own since mount; every AD_EVERY_N_AUTO_ADVANCES-th
  // one shows the ad break first and holds the advance until it ends.
  const autoAdvanceCountRef = useRef(0);
  const pendingAdvanceIndexRef = useRef(null);

  // A song finished by itself: advance to the next one, or show the ad break
  // first for free-tier listeners. The Advertisement component ends the break
  // with `updatecount(0)`, which releases the held advance below.
  const advanceAfterSongEnd = (currentIndex) => {
    if (!isPremium) {
      autoAdvanceCountRef.current += 1;
      if (autoAdvanceCountRef.current % AD_EVERY_N_AUTO_ADVANCES === 0) {
        pendingAdvanceIndexRef.current = currentIndex;
        dispatch(updatecount(1));
        return;
      }
    }
    handleNext("next", currentIndex, true);
  };

  useEffect(() => {
    if (count) return;
    if (pendingAdvanceIndexRef.current === null) return;
    const heldIndex = pendingAdvanceIndexRef.current;
    pendingAdvanceIndexRef.current = null;
    handleNext("next", heldIndex, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [count]);

  // Keep ref in sync (backup for state-driven updates)
  useEffect(() => {
    currentPlayTimeRef.current = currentPlayTime;
  }, [currentPlayTime]);

  // Listen for global stop event
  useEffect(() => {
    const handleStopAllPlayers = (event) => {
      console.log('[Player] Received stop-all-players event from:', event.detail?.source);
      if (song.length > 0 && song[index] && song[index].track && !song[index].track.paused) {
        // Pause the track
        song[index].track.pause();
        setSongPlaying(false);
        // Also pause any WaveSurfer instance
        const wavesurferPlayer = document.querySelector('.wavesurfer-player');
        if (wavesurferPlayer) {
          wavesurferPlayer.dispatchEvent(new CustomEvent('pause-playback'));
        }
      }
    };
    
    window.addEventListener('stop-all-players', handleStopAllPlayers);
    
    return () => {
      window.removeEventListener('stop-all-players', handleStopAllPlayers);
    };
  }, [song, index]);

  useEffect(() => {
    if (songPlaying == null) return;
    playPause(index);
  }, [songPlaying]);

  // Listen for mobile music state updates
  useEffect(() => {
    if (!socket) return;

    const handleMobileMusicState = (data) => {
      console.log('[Player] Received mobile-music-state:', data);

      // Update web player to show what's playing on mobile
      // This allows users to see their mobile playback synced on web

      // Find the song in our current playlist by musicId
      const songIdx = songs.findIndex(s => s._id === data.musicId);

      if (songIdx !== -1 && songIdx !== index) {
        console.log(`[Player] Switching to song index ${songIdx}: ${data.title}`);
        setIndex(songIdx);
      }

      // Update the progress/position
      if (data.position !== undefined && song[songIdx]) {
        setCurrentPlayTime(data.position);
        currentPlayTimeRef.current = data.position;

        // Update the actual audio track position if it exists
        if (song[songIdx]?.track) {
          song[songIdx].track.currentTime = data.position;
        }
      }

      // Update play/pause state to match mobile
      if (data.isPlaying !== undefined) {
        const shouldBePlaying = data.isPlaying;
        const currentlyPlaying = song[songIdx] && !song[songIdx].track?.paused;

        if (shouldBePlaying && !currentlyPlaying) {
          console.log('[Player] Mobile is playing, starting playback on web');
          setSongPlaying(true);
        } else if (!shouldBePlaying && currentlyPlaying) {
          console.log('[Player] Mobile is paused, pausing playback on web');
          setSongPlaying(false);
        }
      }
    };

    console.log('[Player] Registering mobile-music-state listener');
    socket.on('mobile-music-state', handleMobileMusicState);

    return () => {
      console.log('[Player] Removing mobile-music-state listener');
      socket.off('mobile-music-state', handleMobileMusicState);
    };
  }, [socket, songs, song, index]);

  // Server-side play tracking (see playTracking.js): start on play, pause on
  // pause, stop on end/next.
  const handleStartStreaming = async (songIdx) => {
    const s = songs[songIdx];
    try {
      await playTracking.start(s);
      console.log(`🎵 STREAM START: "${s?.title}"`);
    } catch (err) {
      console.error('[Player] Failed to start streaming:', err);
    }
  };

  const handlePauseStreaming = async (songIdx) => {
    try {
      await playTracking.pause(songs[songIdx]);
    } catch (err) {
      console.error('[Player] Failed to pause streaming:', err);
    }
  };

  const handleStopStreaming = async (songIdx) => {
    const s = songs[songIdx];
    try {
      const response = await playTracking.stop(s);
      const result = response?.result;
      if (result) {
        console.log(`🎵 STREAM STOP: "${s.title}" — deducted=${result.balanceDeducted?.toFixed(3)}, elapsed=${(result.elapsedFraction * 100).toFixed(0)}%, balance=${result.remainingBalance}`);
        if (result.remainingBalance !== undefined) {
          localStorage.setItem('streamingBalance', result.remainingBalance);
        }
      }
    } catch (err) {
      console.error('[Player] Failed to stop streaming:', err);
    }
  };


  useEffect(() => {
  const fetchUserSaves = async () => {
    try {
      const response = await ApiService.GET_SAVES('music');
      setUserSaves(response.data || []);
    } catch (error) {
      console.error('Error fetching user saves:', error);
    }
  };
  fetchUserSaves();
}, []);

  // A player rebuilt for the queue it already showed (after a live page) shows
  // the song and waits for the listener instead of playing it.
  const waitOnSongIdRef = useRef(undefined);
  if (waitOnSongIdRef.current === undefined) {
    waitOnSongIdRef.current = songToWaitOn(songs, songIndex);
  }
  useEffect(() => {
    rememberShownQueue(songs);
  }, [songs]);
  useEffect(() => {
    const shownSongId = song[index]?._id;
    if (shownSongId && shownSongId !== waitOnSongIdRef.current) waitOnSongIdRef.current = null;
  }, [index, song]);

  useEffect(() => {
    const audios = [];
    songs.forEach((s, i) => {
      if (s["track"]) {
        // if (i === songs.length - 1) {
        if (!s["track"].paused) {
          // s["track"].pause();
        }
        audios.push(s);
        setSong([...audios]);
        // }
      } else {
        // Create a dummy audio object that doesn't actually play
        // WaveSurfer will handle the actual playback
        const dummyAudio = {
          play: () => console.log("Dummy play - WaveSurfer handles actual playback"),
          pause: () => console.log("Dummy pause - WaveSurfer handles actual pause"),
          volume: 1,
          currentTime: 0,
          duration: 0,
          paused: true
        };
        s["track"] = dummyAudio;
        audios.push(s);
        setSong([...audios]);
      }
    });

    if (songIndex >= 0 && audios.length >= 0) {
      const s = audios;
      if (waitOnSongIdRef.current && s[songIndex]?._id === waitOnSongIdRef.current) {
        setIndex(songIndex);
        updateTrack(songIndex, audios);
        return;
      }
      if (songIndex === 0) {
        // The dummy audio will log but not actually play
        s[songIndex]["track"].play();
        s[songIndex]["track"]["volume"] = volume / 100;
        if (songPlaying && currentPlayTime < 1) {
          handleStartStreaming(songIndex);
        }
        updateTrack(songIndex, audios);
        setSongPlaying(true);
      }
      if (songIndex > 0) {
        if (s[songIndex - 1]["track"])
          if (showAdv) {
            s[songIndex - 1]["track"].pause();
            if (count % 3 === 0) s[songIndex]["track"].pause();
            else s[songIndex]["track"].play();
          } else if (!showAdv) {
            s[songIndex]["track"].play();
          }
        if (songPlaying) {
          handleStartStreaming(songIndex);
        }
        s[songIndex]["track"]["volume"] = volume / 100;
        updateTrack(songIndex, audios);
      }

      setIndex(songIndex);
      s[songIndex]["track"].play();
      setSongPlaying(true);
    }
  }, [songs, showAdv]);

  function playPause(i) {
    if (songPlaying) {
      const s = song;
      const t = s[i];
      song[i].track.play();
      setSong([...s]);
      setTrack({ ...t.track });
      handleStartStreaming(i);
    } else {
      const s = song;
      const t = s[i];
      song[i].track.pause();
      stop();
      setSong([...s]);
      setTrack({ ...t });
      handlePauseStreaming(i);
    }
  }
  var myVar;
  const updateTrack = (currentIndex, song) => {
    const t = song[currentIndex].track;

    setTrack({ ...t });
    song[currentIndex]["end"] = false;
    if (t && !t.paused) {
      if (currentIndex === 0)
        song[currentIndex]["track"]["volume"] = volume / 100;
      // else if (currentIndex > 0) {
      //   song[currentIndex]["track"]["volume"] =
      //     song[currentIndex - 1]["track"]["volume"] / 100;
      // }
      t.addEventListener("timeupdate", function fn() {
        const cT = Math.floor(t.currentTime);
        const du = Math.floor(t.duration);
        const prg = (cT * 100) / du;
        // console.log(prg);
        if (du) {
          setDuration(du);
          setCurrentTime(cT);
          setProgress(prg);
        }
        if (prg === 100) {
          // Check if repeat one is enabled
          if (repeat === 2) {
            // Repeat the current song
            t.currentTime = 0;
            t.play();
            setCurrentTime(0);
            setProgress(0);
          } else {
            setCurrentTime(0);
            setProgress(0);
            setDuration(0);
            t.pause();
            // songs[currentIndex]["track"]["currentTime"] = 0;
            // t.currentTime = 0;
            song[currentIndex]["streamed"] = false;
            song[currentIndex]["end"] = true;
            // const audio = new Audio(song[currentIndex].fileId.filePath);
            // t["track"] = audio;
            // handleChange(true, 0);
            // t.removeEventListener("timeupdate", fn);
          }
        }
        if (
          all &&
          song[currentIndex]["end"] === true &&
          currentIndex < song.length - 1
        ) {
          // const newIndex = currentIndex + 1;
          // setIndex(newIndex);
          // song[newIndex]["track"].play();
          // updateTrack(newIndex, song);
          t.removeEventListener("timeupdate", fn);
          advanceAfterSongEnd(currentIndex);
        }
        if (
          all &&
          song[currentIndex]["end"] === true &&
          currentIndex === song.length - 1
        ) {
          // At the end of playlist
          if (repeat === 1 && song.length > 0) {
            // Repeat all is enabled - go back to first song
            t.removeEventListener("timeupdate", fn);
            handleStopStreaming(currentIndex);
            t.currentTime = 0;
            t.removeEventListener("timeupdate", fn);
            // Start from the beginning of the playlist
            dispatch({ type: "ADD_SONG_INDEX", payload: 0 });
            // Play first song after a short delay to ensure state update
            setTimeout(() => {
              song[0]["track"].play();
              updateTrack(0, song);
            }, 100);
          } else {
            handleStopStreaming(currentIndex);
            t.currentTime = 0;
            setTrack({ ...t });
          }
        }
        // if (repeat === 1 && prg === 100 && t.pause()) {
        //   playPause(songIndex);
        // }
      });
      // t.play();
    }
  };
  const repeatSong = () => {
    // Cycle through repeat modes: 0 (off) -> 1 (repeat all) -> 2 (repeat one) -> 0
    const nextRepeatMode = (repeat + 1) % 3;
    setRepeat(nextRepeatMode);
    localStorage.setItem('playerRepeatMode', nextRepeatMode);
  };

  function stop() {
    clearInterval(myVar);
  }

  const handleEnd = (e, value) => {
    handleSeek(value, true);
  };
  const handleSeek = React.useCallback(
    (value, end) => {
      const t = songs[index]["track"];
      if (t.paused) return;
      if (!seek.started && !end) {
        setSeek((seek) => ({ ...seek, started: true, value: value }));
      } else if (seek.started && end) {
        setSeek({ started: false, value: null });
      }
    },
    [seek.value]
  );
  const handleChange = (newProgress) => {
    handleSeek(newProgress, false);
    const t = songs[index]["track"];

    const cDu = (duration * newProgress) / 100;
    t.currentTime = cDu;
    setTrack({ ...t });
    setCurrentTime(newProgress);
  };

  const handleNext = (type, currentIndex, self) => {
    if (type) {
      const s = songs; //temporary song array
      // s[currentIndex].track.pause();
      // s[currentIndex]["track"]["currentTime"] = 0;
      setTrack({ ...s[currentIndex].track });
      if (type === "next") {
        let i = currentIndex + 1;
        handleStopStreaming(currentIndex);
        s[currentIndex]["track"]["currentTime"] = 0;
        setIndex((indx) => (indx = i));
        s[i]["track"]["volume"] =
          (s[currentIndex]["track"]["volume"] * 100) / 100;
        dispatch(addSongIndexAction(i));
        setSong([...s]);
        setTrack({ ...s[i]["track"] });
      }

      if (type === "prev") {
        let i = currentIndex - 1;
        handleStopStreaming(currentIndex);
        s[currentIndex]["track"]["currentTime"] = 0;
        setIndex((indx) => (indx = i));
        s[i]["track"]["volume"] =
          (s[currentIndex]["track"]["volume"] * 100) / 100;
        dispatch(addSongIndexAction(i));
        setSong([...s]);
        setTrack({ ...s[i].track });
      }
    }
  };
  const handleVolume = (value) => {
    setVolume(value);
    // if (song[index].track) {
    //   const s = song; //temporary song array
    //   s[index].track.volume = value / 100;
    //   setTrack({ ...s[index].track });
    // }
  };

  const handleSaveToggle = (itemId, isSaved) => {
  if (isSaved) {
    setUserSaves([...userSaves, itemId]);
  } else {
    setUserSaves(userSaves.filter(id => id !== itemId));
  }
};
  const handleClose = async (amount, id, type, currency) => {
    if (amount) {
      try {
        const payload = {
          id: id,
          tipAmount: amount,
          type: type,
          currency: currency,
        };
        setLoading(true);
        const response = await ApiService.TIP_ARTIST(payload);
        setLoading(false);
        notifier.success(response["result"]);
        setOpen(false);
        dispatch(getBalance());
      } catch (error) {
        setLoading(false);
        notifier.error(getErrorString(error));
      }
    } else {
      setOpen(false);
    }
  };
  function randomIntFromInterval(min, max) {
    // min and max included
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  const reditectToProfile = (curentSong) => {
    if (typeof curentSong["creatorId"] === "string")
      history.push(`/app/ArtistProfile/${song[index]["creatorId"]}`);
    else if (typeof curentSong["owner"] === "string")
      history.push(`/app/ArtistProfile/${song[index]["owner"]}`);
    else if (typeof curentSong["uploader"] === "string")
      history.push(`/app/ArtistProfile/${song[index]["uploader"]}`);
    else if (typeof curentSong["owner"] === "object")
      history.push(`/app/ArtistProfile/${song[index]["owner"]["_id"]}`);
    else if (typeof curentSong["uploader"] === "object")
      history.push(`/app/ArtistProfile/${song[index]["uploader"]["_id"]}`);
  };

  const getName = () => {
    return song[index]["artist"]
      ? song[index]["artist"]
      : fullName(
          song[index]["owner"]
            ? song[index]["owner"]
            : song[index]["uploader"]
            ? song[index]["uploader"]
            : ""
        );
  };

  // Stop streaming when component unmounts
  useEffect(() => {
    return () => {
      if (playTracking.activeId) {
        // Through playTracking so its heartbeat stops too.
        playTracking.stop({ _id: playTracking.activeId }).catch(() => {});
      }
    };
  }, []);

  const current = song[index];
  const ownerId = current
    ? current["owner"]
      ? current["owner"]["_id"]
      : current["uploader"] && current["uploader"]["_id"]
      ? current["uploader"]["_id"]
      : typeof current["uploader"] === "string"
      ? current["uploader"]
      : ""
    : "";
  const canTip =
    Boolean(current && (current["owner"] || current["uploader"])) && !isOwnerId(ownerId);
  const repeatLabel =
    repeat === 0
      ? t("repeat_off", "Repeat off")
      : repeat === 1
      ? t("repeat_all", "Repeat all")
      : t("repeat_one", "Repeat one");
  const RepeatIcon = repeat === 2 ? RepeatOneRoundedIcon : RepeatRoundedIcon;
  const VolumeIcon =
    volume === 0 ? VolumeOffRoundedIcon : volume < 50 ? VolumeDownRoundedIcon : VolumeUpRoundedIcon;

  const playPrev = () =>
    songs.length > 1 && index >= 1 ? handleNext("prev", index) : handleNext();
  const playNext = () =>
    songs.length > 1 && index < songs.length - 1 ? handleNext("next", index) : handleNext();

  const openSongDetails = () =>
    current["nftId"]
      ? history.push(`/app/SongDetails/${current["nftId"]}?nft=true`)
      : history.push(`/app/SongDetails/${current["_id"]}`);

  const addToStudio = () => {
    setSelectedAlbum(current["_id"]);
    setShowPlaylist(true);
  };

  // Clicking the icon mutes, and clicking again restores the previous level.
  const toggleMute = () => {
    if (volume === 0) {
      handleVolume(lastVolumeRef.current || 50);
    } else {
      lastVolumeRef.current = volume;
      handleVolume(0);
    }
  };

  const toggleQueue = (anchor) => setQueueAnchor(queueAnchor ? null : anchor);
  // The button the queue opened from is gone after a layout switch.
  useEffect(() => setQueueAnchor(null), [isMobile]);

  const renderArtists = () => {
    if (current?.artistData?.length) {
      return current.artistData.map((a, aIndex) => (
        <React.Fragment key={aIndex}>
          {aIndex > 0 ? ", " : null}
          <button
            type="button"
            className="__player_link"
            onClick={() => history.push(`/app/ArtistProfile/${a.uId}`)}
          >
            {a?.displayArtistName}
          </button>
        </React.Fragment>
      ));
    }
    return (
      <button
        type="button"
        className="__player_link"
        onClick={() => reditectToProfile(current)}
      >
        {fullName(
          current["artist"]
            ? current["artist"]
            : typeof current["uploader"] === "object"
            ? current["uploader"]
            : ""
        )}
      </button>
    );
  };

  const queuePopupPosition = () => {
    const anchorRect = queueAnchor?.getBoundingClientRect();
    if (isMobile) {
      // The original mobile popup placement.
      return { bottom: 80, right: anchorRect ? window.innerWidth - anchorRect.right : 60 };
    }
    const barHeight = barRef.current?.getBoundingClientRect().height || 72;
    return {
      bottom: barHeight + 8,
      right: anchorRect ? Math.max(window.innerWidth - anchorRect.right, 8) : 8,
    };
  };

  return (
    <Box>
      {current ? (
        <Box
          ref={barRef}
          component="section"
          aria-label="Now playing"
          className={`__player ${isMobile ? "__player--mobile" : "__player--desktop"}`}
        >
          {/* Mobile and desktop share this tree (null placeholders hold the
              slots) so WaveSurferPlayer, which owns the audio, stays mounted
              when the layout switches at the breakpoint. */}
          <div className="__player_frame">
            <div className="__player_column">
              {isMobile ? (
                <MobileNowPlaying
                  song={current}
                  repeat={repeat}
                  repeatLabel={repeatLabel}
                  onRepeat={repeatSong}
                  queueLabel={t("view_queue")}
                  queueButtonRef={mobileQueueButtonRef}
                  onToggleQueue={() => toggleQueue(mobileQueueButtonRef.current)}
                  addLabel={t("dropMenu.m3")}
                  onAddToStudio={addToStudio}
                  canTip={canTip}
                  onTip={() => setOpen(!open)}
                  onOpenSong={openSongDetails}
                  onOpenArtist={(artistId) => history.push(`/app/ArtistProfile/${artistId}`)}
                  onOpenUploader={() => reditectToProfile(current)}
                />
              ) : null}
              <div className={isMobile ? "__player_box" : "__player_inner"}>
                {isMobile ? null : (
                  <div className="__player_now">
                    <img
                      className="__player_art"
                      src={
                        current["thumbnailImg"]
                          ? current["thumbnailImg"]["filePath"]
                          : Images.rectangle
                      }
                      alt=""
                    />
                    <div className="__player_meta">
                      <div className="__player_title_row">
                        <button
                          type="button"
                          title={current["title"]}
                          className="__player_title __player_link"
                          onClick={openSongDetails}
                        >
                          {current["title"]}
                        </button>
                        <AIBadge show={current["isAIGenerated"]} size="small" />
                      </div>
                      <div className="__player_artists">{renderArtists()}</div>
                    </div>
                    <FavoriteButton
                      className="__player_icon_button __player_like"
                      itemId={current?._id}
                      itemType="music"
                      userSaves={userSaves}
                      onSaveToggle={handleSaveToggle}
                      size={20}
                      color={palette.accent}
                    />
                  </div>
                )}

                <WaveSurferPlayer
                  variant={isMobile ? "mobile" : "desktop"}
                  showAdv={showAdv}
                  volume={volume}
                  url={playableUrl}
                  sampleData={current.sampleData}
                  onMediaError={handleMediaError}
                  startAt={resumeAt.songId === current._id ? resumeAt.time : 0}
                  autoplay={!waitOnSongIdRef.current || waitOnSongIdRef.current !== current._id}
                  handlePlay={(value) => {
                    if (value) waitOnSongIdRef.current = null;
                    setSongPlaying(value);
                  }}
                  handleCurrentTime={(value) => {
                    setCurrentPlayTime(value);
                    currentPlayTimeRef.current = value;
                  }}
                  handleDuration={(value) => {
                    setDuration(value);
                  }}
                  handlePrev={playPrev}
                  handleNext={playNext}
                  currentPlayTime={currentPlayTime}
                  duration={duration}
                  handleEnd={(finalTime) => {
                    if (finalTime) currentPlayTimeRef.current = finalTime;
                    handleStopStreaming(index);
                    if (repeat === 2) {
                      // Repeat one - restart current song
                      const s = song;
                      s[index]["track"].currentTime = 0;
                      s[index]["track"].play();
                      handleStartStreaming(index);
                      setSong([...s]);
                    } else if (songs.length > 1 && index < songs.length - 1) {
                      // Play next song (after an ad break every few songs)
                      advanceAfterSongEnd(index);
                    } else if (repeat === 1 && songs.length > 0) {
                      // Repeat all - go back to first song
                      const s = song;
                      s[index]["track"].pause();
                      s[index]["track"].currentTime = 0;
                      // Use Redux action to update index
                      dispatch({ type: "ADD_SONG_INDEX", payload: 0 });
                      // Play first song after a short delay to ensure state update
                      setTimeout(() => {
                        s[0]["track"].play();
                        updateTrack(0, s);
                      }, 100);
                    } else {
                      // Stop playback
                      handleNext();
                    }
                  }}
                  handleSeek={(value) => {
                    setNewPlayTime(value);
                  }}
                  trailingControl={
                    <IconButton
                      onClick={repeatSong}
                      title={repeatLabel}
                      aria-label={repeatLabel}
                      aria-pressed={repeat > 0}
                      className={`__player_icon_button ${repeat > 0 ? "is-active" : ""}`}
                    >
                      <RepeatIcon />
                    </IconButton>
                  }
                />

                {isMobile ? null : (
                  <div className="__player_actions">
                    {canTip ? (
                      <button
                        type="button"
                        className="__player_tip"
                        title="Send a tip to the artist"
                        aria-label="Send Tip"
                        onClick={() => setOpen(!open)}
                      >
                        <img src={Images.tip_player} alt="" />
                        <span className="__player_tip_label">Send Tip</span>
                      </button>
                    ) : null}
                    <IconButton
                      title={t("dropMenu.m3")}
                      aria-label={t("dropMenu.m3")}
                      className="__player_icon_button"
                      onClick={addToStudio}
                    >
                      <PlaylistAddRoundedIcon />
                    </IconButton>
                    <IconButton
                      title={t("view_queue")}
                      aria-label={t("view_queue")}
                      aria-expanded={Boolean(queueAnchor)}
                      className={`__player_icon_button ${queueAnchor ? "is-active" : ""}`}
                      onClick={(e) => toggleQueue(e.currentTarget)}
                    >
                      <QueueMusicRoundedIcon />
                    </IconButton>
                    <div className="__player_volume">
                      <IconButton
                        title={volume === 0 ? "Unmute" : "Mute"}
                        aria-label={volume === 0 ? "Unmute" : "Mute"}
                        className="__player_icon_button"
                        onClick={toggleMute}
                      >
                        <VolumeIcon />
                      </IconButton>
                      <Slider
                        aria-label="Volume"
                        size="small"
                        value={volume}
                        onChange={(e, value) => handleVolume(value)}
                        className="__player_volume_slider"
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Box>
      ) : null}
      {queueAnchor && current
        ? ReactDOM.createPortal(
            <>
              <Box
                onClick={() => setQueueAnchor(null)}
                sx={{ position: "fixed", inset: 0, zIndex: 1000001 }}
              />
              <Box
                className={isMobile ? "__player_queue_popup--mobile" : "__player_queue_popup"}
                sx={{ position: "fixed", zIndex: 1000002, ...queuePopupPosition() }}
              >
                <PlayQueue
                  handleClose={(e) => {
                    if (e === null) current.track.pause();
                    setQueueAnchor(null);
                  }}
                  onRedirect={(path) => {
                    setQueueAnchor(null);
                    history.push(path);
                  }}
                  playingIndex={index}
                  handleSelectedId={(id) => {
                    setSelectedAlbum(id);
                  }}
                  handleShowPlaylist={(show) => {
                    setShowPlaylist(show);
                  }}
                  currentPlayTime={currentPlayTime}
                />
              </Box>
            </>,
            document.body
          )
        : null}
      {open ? (
        <TipPopup
          artist={getName()}
          id={song[index]["_id"]}
          open={open}
          isArtist={false}
          handleClose={handleClose}
        />
      ) : null}
      {showPlaylist ? (
        <AddMusicToPlaylist
          musicId={selectedId}
          open={showPlaylist}
          handleClose={() => setShowPlaylist(false)}
        />
      ) : null}
      {loading ? <Loader /> : null}
    </Box>
  );
}
